import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';

interface Sub {
  id: string;
  user_id: string;
  plan_id: string;
  status: string;
  subscription_type: string;
  started_at: string;
  expires_at: string;
  created_at: string;
  auto_renew: boolean;
  gateway_id?: string;
  amount?: number;
  billing_cycle?: string;
}

const statusColor: Record<string, string> = {
  active:             'bg-emerald-500/20 text-emerald-400',
  trial:              'bg-violet-500/20 text-violet-400',
  expired:            'bg-orange-500/20 text-orange-400',
  cancelled:          'bg-red-500/20 text-red-400',
  defaulter:          'bg-red-700/20 text-red-400',
  pending:            'bg-yellow-500/20 text-yellow-400',
  overdue:            'bg-orange-700/20 text-orange-400',
  payment_failed:     'bg-red-500/20 text-red-300',
  pending_activation: 'bg-sky-400/20 text-sky-300',
};

const statusLabel: Record<string, string> = {
  active:             'Ativa',
  trial:              'Trial',
  expired:            'Vencida',
  cancelled:          'Cancelada',
  defaulter:          'Inadimplente',
  pending:            'Pendente',
  overdue:            'Expirada',
  payment_failed:     'Pagamento Falhou',
  pending_activation: 'Aguard. Ativação',
};

const typeColor: Record<string, string> = {
  free:  'bg-white/10 text-white/50',
  trial: 'bg-violet-500/20 text-violet-400',
  paid:  'bg-emerald-500/20 text-emerald-400',
};
const typeLabel: Record<string, string> = { free: 'Free', trial: 'Trial', paid: 'Pago' };

const STATUSES = ['all', 'active', 'trial', 'expired', 'cancelled', 'defaulter', 'overdue', 'payment_failed', 'pending_activation'];
const TYPES    = ['all', 'free', 'trial', 'paid'];
const PERIODS  = [
  { label: 'Todos', value: 'all' },
  { label: 'Últimos 7 dias',  value: '7d'  },
  { label: 'Últimos 30 dias', value: '30d' },
  { label: 'Últimos 90 dias', value: '90d' },
];

const AdminAssinaturasPage = () => {
  const [subs, setSubs]             = useState<Sub[]>([]);
  const [profileMap, setProfileMap] = useState<Record<string, string>>({});
  const [planMap, setPlanMap]       = useState<Record<string, { name: string; price_brl: number; slug: string }>>({});
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType]     = useState('all');
  const [filterPeriod, setFilterPeriod] = useState('all');
  const [filterPlan, setFilterPlan]     = useState('all');
  const [plans, setPlans]           = useState<{ id: string; name: string }[]>([]);
  const [detail, setDetail]         = useState<Sub | null>(null);
  const [search, setSearch]         = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    // ── 1. Fetch subscriptions directly ────────────────────────────────────────
    const { data: subsData, error: subsError } = await supabase
      .from('subscriptions')
      .select('id, user_id, plan_id, status, subscription_type, started_at, expires_at, created_at, auto_renew, gateway_id, amount, billing_cycle')
      .order('created_at', { ascending: false });

    if (subsError) {
      console.error('[AdminAssinaturas] subscriptions fetch error:', subsError);
      setError(`Erro ao carregar assinaturas: ${subsError.message}`);
      setLoading(false);
      return;
    }

    const rawSubs = (subsData ?? []) as Sub[];
    setSubs(rawSubs);

    // ── 2. Fetch plans ──────────────────────────────────────────────────────────
    const { data: plansData } = await supabase
      .from('plans')
      .select('id, name, price_brl, slug')
      .order('name');

    const plansArr = (plansData ?? []) as { id: string; name: string; price_brl: number; slug: string }[];
    setPlans(plansArr.map((p) => ({ id: p.id, name: p.name })));
    const pm: Record<string, { name: string; price_brl: number; slug: string }> = {};
    plansArr.forEach((p) => { pm[p.id] = { name: p.name, price_brl: p.price_brl, slug: p.slug }; });
    setPlanMap(pm);

    // ── 3. Fetch profiles for user_ids in subscriptions ─────────────────────────
    const userIds = Array.from(new Set(rawSubs.map((s) => s.user_id).filter(Boolean)));
    if (userIds.length > 0) {
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('id, full_name')
        .in('id', userIds);

      if (profilesError) {
        console.error('[AdminAssinaturas] profiles fetch error:', profilesError);
      } else {
        const prfMap: Record<string, string> = {};
        (profilesData ?? []).forEach((pr: { id: string; full_name: string }) => {
          prfMap[pr.id] = pr.full_name;
        });
        setProfileMap(prfMap);
      }
    }

    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fmt         = (d: string) => d ? new Date(d).toLocaleDateString('pt-BR') : '—';
  const fmtCurrency = (v: number) => v != null ? v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—';

  const isInPeriod = (sub: Sub) => {
    if (filterPeriod === 'all') return true;
    const days = filterPeriod === '7d' ? 7 : filterPeriod === '30d' ? 30 : 90;
    const since = new Date();
    since.setDate(since.getDate() - days);
    return new Date(sub.created_at) >= since;
  };

  const filtered = subs.filter((s) => {
    if (filterStatus !== 'all' && s.status !== filterStatus) return false;
    if (filterType !== 'all' && s.subscription_type !== filterType) return false;
    if (filterPlan !== 'all' && s.plan_id !== filterPlan) return false;
    if (!isInPeriod(s)) return false;
    if (search && !(profileMap[s.user_id] || '').toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const counts = {
    all:                subs.length,
    active:             subs.filter((s) => s.status === 'active').length,
    trial:              subs.filter((s) => s.status === 'trial').length,
    expired:            subs.filter((s) => s.status === 'expired').length,
    cancelled:          subs.filter((s) => s.status === 'cancelled').length,
    defaulter:          subs.filter((s) => s.status === 'defaulter').length,
    payment_failed:     subs.filter((s) => s.status === 'payment_failed').length,
    pending_activation: subs.filter((s) => s.status === 'pending_activation').length,
  };

  return (
    <div>
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-[24px] font-700 text-white">Gestão de Assinaturas</h1>
          <p className="text-white/40 text-[14px] mt-1">Todas as assinaturas da plataforma, incluindo planos gratuitos</p>
        </div>
        <button
          onClick={fetchData}
          className="flex items-center gap-2 px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-white/60 text-[12px] font-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-refresh-line" />Atualizar
        </button>
      </div>

      {/* Error state */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-4 mb-5 flex items-start gap-3">
          <i className="ri-error-warning-line text-red-400 text-lg flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-[13px] font-700 text-red-300 mb-1">Erro ao carregar assinaturas</p>
            <p className="text-[12px] text-red-400/70">{error}</p>
            <button onClick={fetchData} className="mt-2 text-[12px] text-red-300 underline cursor-pointer">Tentar novamente</button>
          </div>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        {[
          { label: 'Total',    value: counts.all,     color: 'text-white' },
          { label: 'Ativas',   value: counts.active,  color: 'text-emerald-400' },
          { label: 'Trial',    value: counts.trial,   color: 'text-violet-400' },
          { label: 'Vencidas', value: counts.expired, color: 'text-orange-400' },
        ].map((c) => (
          <div key={c.label} className="bg-white/5 border border-white/10 rounded-2xl p-4 text-center">
            <p className={`text-[22px] font-700 ${c.color}`}>{c.value}</p>
            <p className="text-[11px] text-white/40 mt-0.5">{c.label}</p>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {[
          { label: 'Inadimplentes',    value: counts.defaulter,          color: 'text-red-400' },
          { label: 'Canceladas',       value: counts.cancelled,          color: 'text-white/40' },
          { label: 'Pagamento Falhou', value: counts.payment_failed,     color: 'text-red-300' },
          { label: 'Aguard. Ativação', value: counts.pending_activation, color: 'text-sky-300' },
        ].map((c) => (
          <div key={c.label} className="bg-white/5 border border-white/10 rounded-2xl p-4 text-center">
            <p className={`text-[22px] font-700 ${c.color}`}>{c.value}</p>
            <p className="text-[11px] text-white/40 mt-0.5">{c.label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-4 mb-5 space-y-3">
        <div className="flex gap-2 flex-wrap items-center">
          <span className="text-[11px] text-white/30 uppercase tracking-wider font-600 mr-1">Status</span>
          {STATUSES.map((s) => (
            <button key={s} onClick={() => setFilterStatus(s)}
              className={`px-3 py-1.5 rounded-xl text-[12px] font-600 transition-colors cursor-pointer whitespace-nowrap ${filterStatus === s ? 'bg-violet-600 text-white' : 'bg-white/5 text-white/50 hover:bg-white/10'}`}
            >
              {s === 'all' ? 'Todos' : (statusLabel[s] ?? s)}
            </button>
          ))}
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <span className="text-[11px] text-white/30 uppercase tracking-wider font-600 mr-1">Tipo</span>
          {TYPES.map((t) => (
            <button key={t} onClick={() => setFilterType(t)}
              className={`px-3 py-1.5 rounded-xl text-[12px] font-600 transition-colors cursor-pointer whitespace-nowrap ${filterType === t ? 'bg-violet-600 text-white' : 'bg-white/5 text-white/50 hover:bg-white/10'}`}
            >
              {t === 'all' ? 'Todos' : typeLabel[t]}
            </button>
          ))}
        </div>
        <div className="flex gap-3 flex-wrap items-center">
          <span className="text-[11px] text-white/30 uppercase tracking-wider font-600 mr-1">Período</span>
          {PERIODS.map((p) => (
            <button key={p.value} onClick={() => setFilterPeriod(p.value)}
              className={`px-3 py-1.5 rounded-xl text-[12px] font-600 transition-colors cursor-pointer whitespace-nowrap ${filterPeriod === p.value ? 'bg-violet-600 text-white' : 'bg-white/5 text-white/50 hover:bg-white/10'}`}
            >
              {p.label}
            </button>
          ))}
          <select value={filterPlan} onChange={(e) => setFilterPlan(e.target.value)}
            className="ml-auto bg-white/5 border border-white/10 text-white/60 text-[12px] rounded-xl px-3 py-1.5 outline-none cursor-pointer"
          >
            <option value="all">Todos os planos</option>
            {plans.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-white/30 text-[13px]" />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar usuário..."
              className="bg-white/5 border border-white/10 rounded-xl pl-8 pr-3 py-1.5 text-white text-[12px] outline-none focus:border-violet-500 placeholder:text-white/20 w-[160px]"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <i className="ri-loader-4-line text-2xl text-violet-400 animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-white/30">
            <i className="ri-file-list-3-line text-3xl mb-2 block" />
            <p className="text-[14px]">Nenhuma assinatura encontrada</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead>
                <tr className="border-b border-white/10">
                  {['Usuário', 'Plano', 'Tipo', 'Status', 'Valor', 'Ciclo', 'Início', 'Vencimento', 'Auto-renovar', 'Ações'].map((h) => (
                    <th key={h} className="text-left px-4 py-3 text-[11px] font-600 text-white/30 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => {
                  const userName = profileMap[s.user_id] || s.user_id?.substring(0, 8) + '...' || '—';
                  const planInfo = s.plan_id ? planMap[s.plan_id] : null;
                  return (
                    <tr key={s.id} className="border-b border-white/5 hover:bg-white/[0.03] transition-colors">
                      <td className="px-4 py-3.5">
                        <p className="text-[13px] text-white font-500 max-w-[140px] truncate">{userName}</p>
                      </td>
                      <td className="px-4 py-3.5 text-[13px] text-white/60">{planInfo?.name || '—'}</td>
                      <td className="px-4 py-3.5">
                        <span className={`text-[11px] font-600 px-2 py-0.5 rounded-full ${typeColor[s.subscription_type] || 'bg-white/10 text-white/40'}`}>
                          {typeLabel[s.subscription_type] || s.subscription_type || 'Free'}
                        </span>
                      </td>
                      <td className="px-4 py-3.5">
                        <span className={`text-[11px] font-600 px-2 py-0.5 rounded-full ${statusColor[s.status] || 'bg-white/10 text-white/40'}`}>
                          {statusLabel[s.status] || s.status}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 text-[13px] text-white/70 font-500">
                        {s.amount != null && Number(s.amount) > 0
                          ? fmtCurrency(Number(s.amount))
                          : (planInfo?.price_brl != null && planInfo.price_brl > 0 ? fmtCurrency(planInfo.price_brl) : 'Grátis')}
                      </td>
                      <td className="px-4 py-3.5 text-[12px] text-white/40 capitalize">
                        {s.billing_cycle === 'annual' ? 'Anual' : s.billing_cycle === 'monthly' ? 'Mensal' : '—'}
                      </td>
                      <td className="px-4 py-3.5 text-[12px] text-white/40 whitespace-nowrap">{fmt(s.started_at)}</td>
                      <td className="px-4 py-3.5 text-[12px] text-white/40 whitespace-nowrap">{fmt(s.expires_at)}</td>
                      <td className="px-4 py-3.5">
                        <span className={`text-[11px] font-600 px-2 py-0.5 rounded-full ${s.auto_renew ? 'bg-emerald-500/20 text-emerald-400' : 'bg-white/10 text-white/30'}`}>
                          {s.auto_renew ? 'Sim' : 'Não'}
                        </span>
                      </td>
                      <td className="px-4 py-3.5">
                        <button onClick={() => setDetail(s)}
                          className="w-7 h-7 flex items-center justify-center bg-white/5 hover:bg-white/15 rounded-lg transition-colors cursor-pointer"
                        >
                          <i className="ri-eye-line text-white/50 text-[13px]" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="px-5 py-3 border-t border-white/5 text-[12px] text-white/30">
          {filtered.length} assinatura{filtered.length !== 1 ? 's' : ''} exibida{filtered.length !== 1 ? 's' : ''}
          {subs.length !== filtered.length && ` (de ${subs.length} total)`}
        </div>
      </div>

      {/* Detail Modal */}
      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-[#1a1a2e] border border-white/10 rounded-2xl w-full max-w-lg shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b border-white/10">
              <h2 className="text-[16px] font-700 text-white">Detalhe da Assinatura</h2>
              <button onClick={() => setDetail(null)} className="w-8 h-8 flex items-center justify-center bg-white/5 hover:bg-white/10 rounded-lg cursor-pointer transition-colors">
                <i className="ri-close-line text-white/60" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              {[
                { label: 'Usuário',              value: profileMap[detail.user_id] || detail.user_id },
                { label: 'Plano',                value: detail.plan_id ? (planMap[detail.plan_id]?.name || '—') : '—' },
                { label: 'Tipo',                 value: typeLabel[detail.subscription_type] || detail.subscription_type || 'Free' },
                { label: 'Status',               value: statusLabel[detail.status] || detail.status },
                { label: 'Valor',                value: detail.amount != null && Number(detail.amount) > 0 ? fmtCurrency(Number(detail.amount)) : 'Grátis' },
                { label: 'Ciclo',                value: detail.billing_cycle === 'annual' ? 'Anual' : detail.billing_cycle === 'monthly' ? 'Mensal' : '—' },
                { label: 'Início',               value: fmt(detail.started_at) },
                { label: 'Vencimento',           value: fmt(detail.expires_at) },
                { label: 'Renovação automática', value: detail.auto_renew ? 'Sim' : 'Não' },
                { label: 'ID',                   value: detail.id },
              ].map((row) => (
                <div key={row.label} className="flex justify-between items-center py-2 border-b border-white/5">
                  <span className="text-[12px] text-white/40">{row.label}</span>
                  <span className="text-[13px] text-white font-500 text-right max-w-[60%] break-all">{row.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAssinaturasPage;
